import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Hourglass, CheckCircle2, Check, X as XIcon, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';

const TYPE_LABELS = {
  ferie: 'Ferie',
  permesso: 'Permesso ROL',
  malattia: 'Malattia',
  aspettativa: 'Aspettativa',
  lutto: 'Permesso lutto',
};
const MONTHS_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const WEEKDAYS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const pad = (n) => String(n).padStart(2, '0');

function chipLabel(req) {
  const name = req.staff_display_name || req.staff_id;
  if (req.unit === 'hours') {
    const tw = req.start_time && req.end_time ? ` ${req.start_time}–${req.end_time}` : ` ${req.duration_hours}h`;
    return `${name} ·${tw}`;
  }
  return name;
}

// Calendario mensile ferie/permessi. Il parent passa solo richieste
// pending + approved (le rifiutate/annullate sono escluse a monte).
// HR/OPS vedono tutto, AM/SM vedono il proprio perimetro (scoping backend).
// `storeNames`: mappa store_id → nome, usata per i badge di sovrapposizione.
export const LeaveCalendar = ({ requests, canApprove, onDecide, storeNames = {} }) => {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [detail, setDetail] = useState(null);

  const { cells, monthLabel } = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const startWeekday = (first.getDay() + 6) % 7; // lun=0
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const mm = pad(cursor.m + 1);
    // Mappa giorno → richieste che lo coprono (confronto su stringhe ISO).
    const byDay = {};
    for (let d = 1; d <= daysInMonth; d++) byDay[`${cursor.y}-${mm}-${pad(d)}`] = [];
    for (const r of requests) {
      const start = r.start_date;
      const end = r.end_date || r.start_date;
      for (const dayKey of Object.keys(byDay)) {
        if (dayKey >= start && dayKey <= end) byDay[dayKey].push(r);
      }
    }
    const out = [];
    for (let i = 0; i < startWeekday; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${cursor.y}-${mm}-${pad(d)}`;
      const reqs = byDay[key];
      // Sovrapposizione: ≥2 assenze nello stesso negozio lo stesso giorno.
      const byStore = {};
      for (const r of reqs) {
        if (!r.store_id) continue;
        (byStore[r.store_id] = byStore[r.store_id] || []).push(r);
      }
      const overlapStores = Object.entries(byStore).filter(([, list]) => list.length >= 2);
      const overlapIds = new Set(overlapStores.flatMap(([, list]) => list.map(r => r.request_id)));
      out.push({ day: d, key, reqs, overlapIds, overlapStores });
    }
    return { cells: out, monthLabel: `${MONTHS_IT[cursor.m]} ${cursor.y}` };
  }, [cursor, requests]);

  // Altre assenze dello stesso negozio che intersecano il periodo della
  // richiesta aperta nel dialog — avviso per chi approva.
  const detailOverlaps = useMemo(() => {
    if (!detail || !detail.store_id) return [];
    const s = detail.start_date;
    const e = detail.end_date || detail.start_date;
    return requests.filter(r =>
      r.request_id !== detail.request_id && r.store_id === detail.store_id &&
      r.start_date <= e && (r.end_date || r.start_date) >= s);
  }, [detail, requests]);

  const todayKey = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const isCurrentMonth = cursor.y === today.getFullYear() && cursor.m === today.getMonth();

  const move = (delta) => {
    setCursor(prev => {
      const d = new Date(prev.y, prev.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  return (
    <div data-testid="leave-calendar">
      {/* Header navigazione + legenda */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => move(-1)} className="px-2" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <p className="font-heading font-bold text-lg px-2 min-w-[180px] text-center" data-testid="cal-month-label">{monthLabel}</p>
          <Button variant="outline" size="sm" onClick={() => move(1)} className="px-2" data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isCurrentMonth && (
            <Button variant="ghost" size="sm" onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })} data-testid="cal-today">
              Oggi
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-300 inline-block" /> In attesa
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300 inline-block" /> Approvata
          </span>
        </div>
      </div>

      {/* Griglia */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {WEEKDAYS_IT.map(w => (
            <div key={w} className="px-2 py-1.5 text-[11px] uppercase tracking-wide font-semibold text-slate-500 text-center">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={`empty-${idx}`} className="min-h-[88px] border-b border-r border-slate-100 bg-slate-50/50" />;
            const isToday = cell.key === todayKey;
            return (
              <div
                key={cell.key}
                className={`min-h-[88px] border-b border-r border-slate-100 p-1 ${isToday ? 'bg-blue-50/60' : ''}`}
                data-testid={`cal-day-${cell.key}`}
              >
                <p className={`text-[11px] font-semibold mb-1 px-0.5 flex items-center gap-1 ${isToday ? 'text-blue-700' : 'text-slate-500'}`}>
                  {cell.day}{isToday && <span className="ml-1 font-normal">· oggi</span>}
                  {cell.overlapIds.size > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[9px] font-bold text-red-700 bg-red-100 border border-red-300 rounded-sm px-1 py-px ml-auto"
                      title={cell.overlapStores.map(([sid, list]) =>
                        `${storeNames[sid] || sid}: ${list.length} assenze insieme`).join(' · ')}
                      data-testid={`cal-overlap-${cell.key}`}
                    >
                      <AlertTriangle className="w-2.5 h-2.5" />
                      {cell.overlapIds.size}
                    </span>
                  )}
                </p>
                <div className="space-y-0.5">
                  {cell.reqs.map(r => (
                    <button
                      key={`${cell.key}-${r.request_id}`}
                      type="button"
                      onClick={() => setDetail(r)}
                      className={`block w-full text-left text-[10px] leading-tight px-1 py-0.5 border rounded-sm truncate transition-colors ${
                        r.status === 'pending'
                          ? 'bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200'
                          : 'bg-emerald-100 text-emerald-900 border-emerald-300 hover:bg-emerald-200'
                      } ${cell.overlapIds.has(r.request_id) ? 'ring-1 ring-red-500 border-red-400' : ''}`}
                      title={`${r.staff_display_name || r.staff_id} · ${TYPE_LABELS[r.type] || r.type} (${r.status === 'pending' ? 'in attesa' : 'approvata'})`}
                      data-testid={`cal-chip-${r.request_id}`}
                    >
                      {chipLabel(r)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dettaglio richiesta */}
      {detail && (
        <Dialog open onOpenChange={(o) => { if (!o) setDetail(null); }}>
          <DialogContent className="max-w-md" data-testid="cal-detail-dialog">
            <DialogHeader>
              <DialogTitle>{detail.staff_display_name || detail.staff_id}</DialogTitle>
              <DialogDescription className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm bg-slate-50 text-slate-700 border-slate-300">
                  {TYPE_LABELS[detail.type] || detail.type}
                </span>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${
                  detail.status === 'pending'
                    ? 'bg-amber-50 text-amber-800 border-amber-300'
                    : 'bg-emerald-50 text-emerald-800 border-emerald-300'
                }`}>
                  {detail.status === 'pending' ? <Hourglass className="w-2.5 h-2.5" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
                  {detail.status === 'pending' ? 'In attesa' : 'Approvata'}
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm text-slate-700 space-y-1.5">
              <p>
                <span className="text-slate-500">Periodo:</span>{' '}
                {detail.unit === 'hours'
                  ? `${detail.start_date}${detail.start_time && detail.end_time ? ` (${detail.start_time}–${detail.end_time})` : ''} · ${detail.duration_hours}h`
                  : (detail.end_date && detail.end_date !== detail.start_date
                    ? `${detail.start_date} → ${detail.end_date} · ${detail.duration_days} gg`
                    : `${detail.start_date} · 1 gg`)}
              </p>
              {detail.reason && <p><span className="text-slate-500">Motivo:</span> {detail.reason}</p>}
              {detailOverlaps.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-2.5 mt-2" data-testid="cal-detail-overlap-warning">
                  <p className="text-xs font-semibold text-red-800 flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Sovrapposizione{storeNames[detail.store_id] ? ` — ${storeNames[detail.store_id]}` : ' nello stesso negozio'}
                  </p>
                  <ul className="text-xs text-red-700 space-y-0.5">
                    {detailOverlaps.map(o => (
                      <li key={o.request_id}>
                        {o.staff_display_name || o.staff_id} · {o.end_date && o.end_date !== o.start_date
                          ? `${o.start_date} → ${o.end_date}` : o.start_date}
                        {' '}({o.status === 'pending' ? 'in attesa' : 'approvata'})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {detail.decided_by_email && detail.status !== 'pending' && (
                <p className="text-xs text-slate-500">
                  Decisa da {detail.decided_by_email} il {new Date(detail.decided_at).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
            <DialogFooter>
              {detail.status === 'pending' && canApprove ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { onDecide(detail, 'rejected'); setDetail(null); }}
                    className="gap-1 border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="cal-detail-reject"
                  >
                    <XIcon className="w-3.5 h-3.5" /> Rifiuta
                  </Button>
                  <Button
                    onClick={() => { onDecide(detail, 'approved'); setDetail(null); }}
                    className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="cal-detail-approve"
                  >
                    <Check className="w-3.5 h-3.5" /> Approva
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setDetail(null)}>Chiudi</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
